/**
 * cell-text.ts — komórka listy jako TEKST do pliku CSV (E16).
 *
 * Plik ma zawierać to, co użytkownik widzi na ekranie (etykiety słowników, nazwy firm, formaty dat
 * i kwot, „Tak"/„Nie"), a ekran składa komórki w przeglądarce — serwer zna tylko kody i
 * identyfikatory. Dlatego tekst wyciągamy z tego samego `renderCell` kolumny: element React
 * przechodzimy statycznie (bez renderowania), znaczniki DOM → tekst dzieci. Komórka będąca
 * komponentem z hookiem (np. etykieta relacji dociągana zapytaniem) nie da się wywołać poza
 * drzewem React — wtedy bierzemy surową wartość z accessora.
 */

import { Fragment, isValidElement, type ReactNode } from 'react';

import type { ColumnDefinition } from '@/components/configurable-table';

/** Myślnik „brak wartości" z ekranu — w pliku pusta komórka. */
const EMPTY_PLACEHOLDERS = new Set(['—', '–', '-']);

function joinTexts(parts: string[], allElements: boolean): string {
  const nonEmpty = parts.filter((p) => p !== '');
  // Same znaczniki obok siebie (chipy grup/tagów) → lista po przecinku; tekst z wstawkami
  // (`{a} {b}`) → sklejenie bez separatora, tak jak widać na ekranie.
  return nonEmpty.join(allElements ? ', ' : '');
}

/** Tekst z drzewa React (statycznie, bez renderowania). Brak wartości → pusty tekst. */
export function reactNodeToText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') {
    const text = String(node).trim();
    return EMPTY_PLACEHOLDERS.has(text) ? '' : String(node);
  }
  if (Array.isArray(node)) {
    const parts = node.map(reactNodeToText);
    return joinTexts(parts, node.every(isValidElement));
  }
  if (isValidElement(node)) {
    const props = node.props as { children?: ReactNode; 'aria-label'?: unknown };
    if (typeof node.type === 'string' || node.type === Fragment) {
      return reactNodeToText(props.children);
    }
    // Komponent funkcyjny: nie wywołujemy go (może używać hooków). Etykieta dostępności
    // (`aria-label`, np. checkbox Tak/Nie) to tekst, który ekran „mówi" o komórce.
    if (typeof props['aria-label'] === 'string') return props['aria-label'];
    return reactNodeToText(props.children);
  }
  return '';
}

/** Surowa wartość komórki jako tekst (gdy kolumna nie ma tekstu do wyciągnięcia z renderCell). */
function valueToText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Tak' : 'Nie';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (value instanceof Date) return value.toLocaleString('pl-PL');
  if (Array.isArray(value)) return value.map(valueToText).filter(Boolean).join(', ');
  return '';
}

/** Tekst jednej komórki kolumny tabeli: z `renderCell` (jak ekran), a gdy pusty — z wartości. */
export function columnCellText<TRow>(column: ColumnDefinition<TRow>, row: TRow): string {
  const value =
    typeof column.accessor === 'function'
      ? column.accessor(row)
      : (row as Record<string, unknown>)[column.accessor];
  if (column.renderCell) {
    const fromNode = reactNodeToText(column.renderCell(value, row, 0));
    if (fromNode !== '') return fromNode;
    // Pusty tekst z renderCell bywa prawdą („—" = brak wartości) albo komponentem z hookiem —
    // odróżnia je surowa wartość: jest pusta → pusta komórka, jest → pokaż ją.
  }
  return valueToText(value);
}

/** Kolumna pliku: identyfikator, nagłówek jak na ekranie i funkcja tekstu komórki. */
export interface ExportColumn<TRow> {
  id: string;
  header: string;
  text: (row: TRow) => string;
  /** Surowa wartość komórki (accessor kolumny) — wejście reguł formatowania listy. */
  value?: (row: TRow) => unknown;
}

export interface ExportColumnsOptions<TRow> {
  /** Nadpisanie tekstu komórki wg id kolumny (np. etykieta z listy opcji wczytanej na ekranie). */
  textOverrides?: Readonly<Record<string, (row: TRow) => string>>;
  /** Własne etykiety kolumn z układu listy (`customLabel`) — wygrywają z domyślnym nagłówkiem. */
  customLabels?: Readonly<Record<string, string | undefined>>;
}

/**
 * Kolumny pliku = kolumny WIDOCZNE w tabeli, w kolejności z ekranu (`visibleIds` — efektywny
 * zbiór z `ConfigurableTable.onVisibleColumnsChange`), z nagłówkami jak na ekranie. Id spoza
 * `columns` pomijane. `visibleIds === null` (tabela jeszcze nie zgłosiła widoczności) →
 * kolumny domyślnie widoczne w kolejności definicji.
 */
export function exportColumnsFromTable<TRow>(
  columns: readonly ColumnDefinition<TRow>[],
  visibleIds: readonly string[] | null,
  options: ExportColumnsOptions<TRow> = {},
): ExportColumn<TRow>[] {
  const { textOverrides = {}, customLabels = {} } = options;
  const byId = new Map(columns.map((c) => [c.id, c]));
  const ordered =
    visibleIds === null
      ? columns.filter((c) => c.defaultVisible !== false)
      : visibleIds.map((id) => byId.get(id)).filter((c): c is ColumnDefinition<TRow> => !!c);
  return ordered.map((column) => {
    const override = textOverrides[column.id];
    return {
      id: column.id,
      header: customLabels[column.id]?.trim() || column.header,
      text: override ?? ((row: TRow) => columnCellText(column, row)),
      value: (row: TRow) =>
        typeof column.accessor === 'function'
          ? column.accessor(row)
          : (row as Record<string, unknown>)[column.accessor],
    };
  });
}
